import { registerSessionReset, sessionIsCurrent, sessionTicket } from '../../../../utils/sessionLifecycle';

// Generic "cache + dedupe in-flight + skip caching empty/failed results"
// primitive — factors out a pattern that was hand-duplicated per fetcher in
// helpers.js (_fetchAgents, _fetchSRMUsers, _fetchQuickReplies,
// _fetchTemplates, fetchMiscOptions, fetchChatFilters).
//
// `fetchFn(key)` is called at most once per distinct key at a time; a second
// call for the same key while the first is still in flight shares that same
// promise instead of firing a duplicate request. A resolved value is only
// cached when `isCacheable` accepts it (default: truthy and, for arrays,
// non-empty) — mirrors every hand-written cache in helpers.js only ever
// caching a real, non-empty result. A rejection resolves to `fallback`
// (default null) and is never cached, so the next call retries.
export function createCachedFetcher(fetchFn, { fallback = null, isCacheable = defaultIsCacheable } = {}) {
  const cache = new Map();
  const inFlight = new Map();
  registerSessionReset(() => { cache.clear(); inFlight.clear(); });

  return function fetcher(key, { force = false } = {}) {
    const ticket = sessionTicket();
    if (!force) {
      if (cache.has(key)) return Promise.resolve(cache.get(key));
      if (inFlight.has(key)) return inFlight.get(key);
    }

    let raw;
    try {
      raw = Promise.resolve(fetchFn(key));
    } catch (err) {
      raw = Promise.reject(err);
    }

    const promise = raw
      .then(result => {
        if (!sessionIsCurrent(ticket)) return fallback;
        if (inFlight.get(key) === promise) inFlight.delete(key);
        if (isCacheable(result)) cache.set(key, result);
        return result;
      })
      .catch(() => {
        if (inFlight.get(key) === promise) inFlight.delete(key);
        return fallback;
      });

    inFlight.set(key, promise);
    return promise;
  };
}

function defaultIsCacheable(result) {
  if (!result) return false;
  if (Array.isArray(result)) return result.length > 0;
  return true;
}
